/**
 * Green Rush — o motor da máquina de arcade da Sala de Descanso.
 *
 * Um C63 sobe uma estrada que não acaba. Desvia-se do que vier à frente, apanha
 * os bidões, e a corrida termina na primeira batida. É tudo — e o jogo inteiro
 * cabe nesta simulação.
 *
 * ── A decisão que manda em todo o resto: só há UM autor ────────────────────
 *
 * Numa call de seis pessoas, joga uma de cada vez e as outras VÊEM. Isso podia
 * fazer-se transmitindo o estado dez vezes por segundo — dez obstáculos, quatro
 * números cada — e os espectadores interpolavam entre pacotes. Não é o que está
 * aqui, e a alternativa é melhor por duas razões que não são de gosto:
 *
 *  1. Os espectadores veem a 60 fps, não a 10 com adivinhação pelo meio. Cada
 *     ecrã corre ESTA simulação com os MESMOS inputs e chega ao mesmo pixel.
 *  2. A pontuação deixa de ser uma opinião. Ninguém anuncia "fiz 12 400": cada
 *     ecrã chega a 12 400 sozinho. Um recorde não precisa de ser acreditado.
 *
 * O preço é uma regra dura: esta simulação tem de dar EXACTAMENTE o mesmo
 * resultado em qualquer motor de JavaScript, para sempre. Daí as três leis do
 * ficheiro, que não são preferências de estilo:
 *
 *  • TUDO SÃO INTEIROS. Não há uma divisão fraccionária, nem uma raiz, nem um
 *    seno, em lado nenhum do caminho que decide onde está uma coisa ou se houve
 *    batida. `Math.sin` e companhia têm precisão definida pela IMPLEMENTAÇÃO —
 *    dois browsers podem discordar num bit, e um bit chega para um ecrã ver o
 *    carro passar e o outro vê-lo bater.
 *  • O ACASO É REPRODUZÍVEL. O gerador é um mulberry32 escrito com `Math.imul`,
 *    aritmética de 32 bits pura. `Math.random()` aqui dentro seria o fim: cada
 *    ecrã teria a sua estrada.
 *  • O ESTADO É NOVO A CADA PASSO. Mutar em sítio faria o React perder a
 *    mudança e faria um passo repetido corromper a corrida.
 *
 * ── Unidades ──────────────────────────────────────────────────────────────
 *
 * A estrada tem `RUSH_W` de largura e vê-se `RUSH_VIEW` para a frente. Não são
 * pixels de nada: é o desenho que se estica para o tamanho que tiver. `y` cresce
 * PARA BAIXO (como no ecrã), o carro está parado em `RUSH_CAR.y` e é o mundo
 * que sobe contra ele — é assim que se faz uma estrada infinita sem números
 * infinitos.
 */
/** Passos por segundo. O relógio do jogo é o tick, nunca o `performance.now()`:
 *  um jogo que avance por tempo decorrido avança diferente em cada máquina. */
export const RUSH_TPS = 60;
/** Largura da estrada e quanto se vê à frente. */
export const RUSH_W = 1000;
export const RUSH_VIEW = 2000;
/**
 * O carro do jogador — um C63 visto de cima.
 *
 * `hitW` é mais estreita que `w` de propósito, e não é para facilitar: o carro
 * tem espelhos e guarda-lamas a sair do corpo, e perder uma corrida porque a
 * ponta de um espelho roçou num cone é a diferença entre um jogo tenso e um jogo
 * injusto. `hitPad` corta o nariz e a traseira em bico pela mesma razão.
 *
 * Estes números mandam na ARTE, nunca o contrário: o `Arcade` desenha o PNG
 * dentro da caixa que sai daqui, sem olhar para as dimensões do ficheiro. Trocar
 * a imagem por outra de outro tamanho encolhe-a ou estica-a — nunca a desalinha
 * do que bate, que foi exactamente o defeito que houve quando o desenho e a
 * colisão eram duas contas separadas.
 *
 * `180` tem um tecto por cima e um chão por baixo, e ambos são a estrada. Por
 * cima: a faixa mede 200, e um carro que a encha deixa de se ler COMO estando
 * numa faixa — a 190 já ocupa 95% dela e a leitura de relance que este jogo pede
 * (ver «`RUSH_LANES`») começa a desfazer-se. Por baixo: o trânsito tem 170 de
 * largo, e o carro do jogador ser mais estreito do que aquilo de que se desvia
 * lia-se mal. `y` fica onde estava DE PROPÓSITO ao crescer a altura: `y` é o
 * NARIZ, portanto o que se vê à frente — o tempo de reacção — não muda, e o
 * carro cresce para trás, para as 250 unidades de estrada que sobravam atrás.
 */
export const RUSH_CAR = { w: 180, h: 360, y: 1450, hitW: 140, hitPad: 31 };
/**
 * As cinco posições onde nasce um obstáculo.
 *
 * Fixas, e não um número ao calhas entre 0 e 1000. Um obstáculo que possa nascer
 * a QUALQUER x obriga a ler a estrada em contínuo, e o olho não faz isso a 44
 * unidades por tick — lê faixas. Cinco faixas de 200 dizem-se de relance, e é
 * essa leitura instantânea que separa um jogo de reflexos de um jogo de sorte.
 */
export const RUSH_LANES = [100, 300, 500, 700, 900];
/** Quanto anda o carro de lado por tick. A 20, atravessa a estrada em ~0,7 s:
 *  responde já, mas não teleporta — dá para errar a manobra, que é o ponto. */
export const RUSH_MOVE = 20;
/** Velocidade inicial, tecto, e de quantos em quantos ticks sobe um degrau. */
export const RUSH_V0 = 15;
export const RUSH_VMAX = 44;
export const RUSH_ACC = 150;
/** O que vale um bidão de combustível. */
export const RUSH_CAN = 400;
/** A velocidade a que o carro lento à frente também anda. Sem isto ele seria um
 *  muro parado no meio da estrada; assim aproxima-se devagar e dá tempo. */
export const RUSH_TRAFFIC = 9;
export const RUSH_CONE = 0;
export const RUSH_HOLE = 1;
export const RUSH_CAR_NPC = 2;
export const RUSH_CANISTER = 3;
/** Tamanho de cada um. O `y` de um obstáculo é o seu TOPO. */
export const RUSH_SIZES = {
    0: { w: 80, h: 80 },
    1: { w: 170, h: 130 },
    2: { w: 170, h: 290 },
    3: { w: 90, h: 110 },
};
/**
 * Com que frequência sai cada coisa, em partes de cem.
 *
 * O bidão pesa tanto como o buraco porque é ele que dá ao jogo uma segunda
 * decisão: sem bónus, joga-se sempre pela faixa mais vazia e a corrida é uma
 * linha recta. Com bónus, vale a pena ir buscar — e ir buscar é onde se bate.
 */
const PESOS = [
    [0, 40],
    [1, 18],
    [2, 24],
    [3, 18],
];
/** A caixa de um obstáculo: o que se vê E o que bate (são a mesma). */
export function rushObsBox(o) {
    const t = RUSH_SIZES[o.k];
    return { x0: o.x - Math.floor(t.w / 2), y0: o.y, w: t.w, h: t.h };
}
/** A caixa DESENHADA do carro — a arte toda, pára-choques incluídos. */
export function rushCarBox(x) {
    return { x0: x - Math.floor(RUSH_CAR.w / 2), y0: RUSH_CAR.y, w: RUSH_CAR.w, h: RUSH_CAR.h };
}
/**
 * A caixa que BATE — estritamente dentro da desenhada.
 *
 * É de propósito mais pequena que a arte: os cantos de um carro visto de cima
 * são vazios, e uma caixa colada à arte faz o jogador morrer de raspão em coisas
 * que passaram ao lado. Perdoar é o que faz o jogo ler como justo.
 */
export function rushHitBox(x) {
    const e = RUSH_CAR.hitPad;
    return {
        x0: x - Math.floor(RUSH_CAR.hitW / 2),
        y0: RUSH_CAR.y + e,
        w: 2 * Math.floor(RUSH_CAR.hitW / 2),
        h: RUSH_CAR.h - 2 * e,
    };
}
/** Dois rectângulos que se sobrepõem. Encostar não conta — só atravessar. */
export function rushOverlap(a, b) {
    return a.x0 < b.x0 + b.w && b.x0 < a.x0 + a.w && a.y0 < b.y0 + b.h && b.y0 < a.y0 + a.h;
}
// ── O acaso, reproduzível ───────────────────────────────────
/**
 * mulberry32 — um passo do gerador. Devolve [novo estado, valor de 32 bits].
 *
 * Escolhido por caber em `Math.imul` e deslocamentos: aritmética de inteiros de
 * 32 bits, que é a única parte do JavaScript onde dois motores TÊM de concordar
 * bit a bit. Um gerador com floats seria mais bonito e não serviria para nada.
 */
function passoRng(estado) {
    const seguinte = (estado + 0x6d2b79f5) | 0;
    let t = seguinte;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return [seguinte, (t ^ (t >>> 14)) >>> 0];
}
/** Um inteiro em [0, n). O módulo enviesa as últimas contagens em 2^-32 — a
 *  diferença é invisível e o remédio (rejeição) custava um ciclo indefinido. */
function sorteio(estado, n) {
    const [e, v] = passoRng(estado);
    return [e, v % n];
}
/**
 * A semente de uma corrida, a partir do número da corrida e de quem a joga.
 *
 * É uma FNV-1a sobre a mesma string em todos os ecrãs, e é o que permite não
 * transmitir semente nenhuma: cada cliente calcula-a. Um sorteio real teria de
 * viajar, e um pacote perdido punha dois ecrãs a ver estradas diferentes com a
 * mesma cara de quem tem razão.
 */
export function rushSeed(runNo, id) {
    let h = 0x811c9dc5;
    const texto = `${runNo}:${id}`;
    for (let i = 0; i < texto.length; i += 1) {
        h ^= texto.charCodeAt(i) & 0xff;
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
// ── A simulação ─────────────────────────────────────────────
export function rushStart(seed) {
    return {
        tick: 0,
        rng: seed | 0,
        x: RUSH_W / 2,
        vel: RUSH_V0,
        score: 0,
        cans: 0,
        obs: [],
        next: 30,
        born: 0,
        over: 0,
        hit: -1,
    };
}
/** De quantos em quantos ticks nasce um obstáculo, neste ponto da corrida. */
function intervalo(tick) {
    return Math.max(24, 64 - Math.floor(tick / 100));
}
/** A que velocidade DESCE cada coisa: o trânsito também anda para a frente. */
function descida(k, vel) {
    return k === RUSH_CAR_NPC ? vel - RUSH_TRAFFIC : vel;
}
function sorteiaKind(estado) {
    const [e, v] = sorteio(estado, 100);
    let acc = 0;
    for (const [k, peso] of PESOS) {
        acc += peso;
        if (v < acc)
            return [e, k];
    }
    return [e, RUSH_CONE];
}
/**
 * Um passo do mundo. Devolve o estado seguinte — sempre novo, sempre inteiro.
 *
 * A ordem das quatro coisas que acontecem aqui não é arbitrária, e trocá-la
 * muda o jogo: primeiro vira-se o volante, DEPOIS o mundo desce, e só então se
 * mede a batida. Medir antes de descer daria a quem carregasse no último tick
 * um obstáculo que já lá não estava.
 */
export function rushStep(s, dir) {
    if (s.over)
        return s;
    const tick = s.tick + 1;
    const vel = Math.min(RUSH_VMAX, RUSH_V0 + Math.floor(tick / RUSH_ACC));
    // 1. O volante. O carro não sai da estrada — encosta e fica.
    const meia = Math.floor(RUSH_CAR.w / 2);
    const x = Math.max(meia, Math.min(RUSH_W - meia, s.x + dir * RUSH_MOVE));
    // 2. O mundo desce, e o que passou por baixo do ecrã deixa de existir.
    const descidos = [];
    for (const o of s.obs) {
        const y = o.y + descida(o.k, vel);
        if (y < RUSH_VIEW + 200)
            descidos.push({ ...o, y });
    }
    // 3. Nasce o que tiver de nascer.
    let rng = s.rng;
    let born = s.born;
    let next = s.next;
    if (tick >= s.next) {
        const [e1, k] = sorteiaKind(rng);
        const [e2, lane] = sorteio(e1, RUSH_LANES.length);
        rng = e2;
        const tam = RUSH_SIZES[k];
        descidos.push({ id: born, k, x: RUSH_LANES[lane] ?? RUSH_W / 2, y: -tam.h });
        born += 1;
        next = tick + intervalo(tick);
    }
    // 4. A batida — ou o bidão. A caixa sai das MESMAS funções que o ecrã usa
    //    para desenhar; ver `rushHitBox`.
    const caixa = rushHitBox(x);
    const obs = [];
    let cans = s.cans;
    let bonus = 0;
    let hit = -1;
    for (const o of descidos) {
        const toca = rushOverlap(caixa, rushObsBox(o));
        if (!toca) {
            obs.push(o);
            continue;
        }
        if (o.k === RUSH_CANISTER) {
            // O bidão desaparece ao ser apanhado. Deixá-lo no mundo dava-o outra vez
            // no tick seguinte, e uma passagem lenta por cima valia mil pontos.
            cans += 1;
            bonus += RUSH_CAN;
            continue;
        }
        obs.push(o);
        if (hit === -1)
            hit = o.k;
    }
    return {
        tick,
        rng,
        x,
        vel,
        score: s.score + vel + bonus,
        cans,
        obs,
        next,
        born,
        over: hit === -1 ? 0 : tick,
        hit,
    };
}
/**
 * Corre uma lista de direcções do princípio ao fim.
 *
 * Existe para os testes e para o replay: é isto que prova que a mesma semente
 * com os mesmos volantes dá a mesma corrida. Um espectador NÃO usa esta função
 * (avança tick a tick, à medida que os pacotes chegam) — mas tem de chegar
 * exactamente ao mesmo sítio que ela.
 */
export function rushRun(seed, dirs) {
    let s = rushStart(seed);
    for (const d of dirs)
        s = rushStep(s, d);
    return s;
}
/** A pontuação como se mostra: metros, não unidades de estrada. */
export function rushMeters(score) {
    return Math.floor(score / 10);
}
/** O nome do que nos apanhou, para a linha de fim de corrida. */
export function rushHitLabel(k) {
    switch (k) {
        case RUSH_CONE:
            return "um cone";
        case RUSH_HOLE:
            return "um buraco";
        case RUSH_CAR_NPC:
            return "a carrinha da frente";
        default:
            return "nada";
    }
}
//# sourceMappingURL=rush.js.map